import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { AuthService } from './auth.service';
import { SharedDataService } from './shared-data.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private CURRENT_PAGE_KEY = 'currentPage';
  private currentPageSubject: BehaviorSubject<string>;

  // For new customers, the flow includes selection, checkout, stripe, and success.
  private readonly newFlow: string[] = ['selection', 'checkout', 'stripe', 'success'];
  // For existing customers, skip the selection step.
  private readonly existingFlow: string[] = ['checkout', 'stripe', 'success'];

  constructor(
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private sharedDataService: SharedDataService
  ) {
    const storedPage = this.localStorageService.getItem(this.CURRENT_PAGE_KEY) || 'selection';
    this.currentPageSubject = new BehaviorSubject<string>(storedPage);
  }

  /**
   * Initializes navigation state using the query parameter success flag and the
   * account type (new or existing). If the URL indicates success, we always set the page to "success".
   */
  initializeNavigation(isSuccess: boolean, isExistingCustomer: boolean, skipSelection: boolean = false): void {
    let storedPage = this.localStorageService.getItem(this.CURRENT_PAGE_KEY);
    if(storedPage === 'success') {
      const returnURL = this.sharedDataService.getReturnURL();
      if (returnURL) {
        window.location.href = returnURL;
        return;
      }
    }

    if (isSuccess) {
      // Override any stored value if the purchase was successful.
      storedPage = 'success';
    } else if (skipSelection) {
      // Direct-entry URL parameters resolved a plan; jump past the selection page.
      storedPage = 'checkout';
    } else {
      if (isExistingCustomer) {
        // For existing users, if no page is stored or if it's "selection" (which they shouldn't see),
        // default to "checkout".
        if (!storedPage || storedPage === 'selection') {
          storedPage = 'checkout';
        }
      } else {
        // For new users, default to "selection" if nothing is stored.
        if (!storedPage) {
          storedPage = 'selection';
        }
      }
    }
    this.setCurrentPage(storedPage);
  }

  setCurrentPage(page: string): void {
    // Enforce that existing customers never see the "selection" page.
    if (this.authService.isExistingCustomerMode() && page === 'selection') {
      page = 'checkout';
    }
    if (this.currentPageSubject.value !== page) {
      this.localStorageService.setItem(this.CURRENT_PAGE_KEY, page);
      this.currentPageSubject.next(page);
    }
  }

  navigateForward(): void {
    const flow = this.authService.isExistingCustomerMode() ? this.existingFlow : this.newFlow;
    const currentPage = this.getCurrentPageValue();
    const currentIndex = flow.indexOf(currentPage);
    if (currentIndex >= 0 && currentIndex < flow.length - 1) {
      const nextPage = flow[currentIndex + 1];
      this.setCurrentPage(nextPage);
    }
  }

  navigateBack(): void {
    const currentPage = this.getCurrentPageValue();
    const flow = this.authService.isExistingCustomerMode() ? this.existingFlow : this.newFlow;

    // Handle refresh or back from success page with a return URL
    if (currentPage === 'success') {
      const returnURL = this.sharedDataService.getReturnURL();
      if (returnURL) {
        this.localStorageService.clearData(); // clear all stored data after successful purchase
        window.location.href = returnURL;
        return;
      }
    }

    // Existing customer + on checkout → handle via return URL
    if (this.authService.isExistingCustomerMode() && currentPage === 'checkout') {
      const returnURL = this.sharedDataService.getReturnURL();
      if (returnURL) {
        this.localStorageService.clearData(); // Optionally clear stored data after redirecting.
        window.location.href = returnURL;
        return;
      } else {
        this.setCurrentPage('checkout');
      }
    }

    const currentIndex = flow.indexOf(currentPage);
    if (currentIndex > 0) {
      const previousPage = flow[currentIndex - 1];
      this.setCurrentPage(previousPage);
    }
  }


  getCurrentPage(): Observable<string> {
    return this.currentPageSubject.asObservable();
  }

  getCurrentPageValue(): string {
    return this.currentPageSubject.value;
  }
}
